import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Save, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle,
  Globe,
  Mail,
  Shield,
  Bell,
  Key,
  Palette,
  Eye,
  EyeOff,
  Server,
  Clock,
  FileText,
  MapPin,
  Brain
} from 'lucide-react';
import { adminAPI } from '../../services/api';

// Input field components - defined outside to prevent re-rendering issues
const InputField = ({ label, type = 'text', value, onChange, placeholder, required = false }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
    />
  </div>
);

const TextareaField = ({ label, value, onChange, placeholder, rows = 3 }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
      {label}
    </label>
    <textarea
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
    />
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
      {label}
    </label>
    <select
      value={value || ''}
      onChange={onChange}
      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  </div>
);

const SettingsManagement = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showFirebaseKey, setShowFirebaseKey] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveStatus, setSaveStatus] = useState(''); // 'success', 'error', ''
  const [settings, setSettings] = useState({
    general: {
      siteTitle: 'Niswa Info Tech',
      siteTagline: 'Leading Digital Agency for Web Development, Mobile Apps & Digital Marketing',
      siteName: 'Niswa Info Tech',
      siteDescription: 'Leading Digital Agency for Web Development, Mobile Apps & Digital Marketing',
      siteUrl: 'https://niswainfo.com',
      adminEmail: 'admin@niswainfo.com',
      contactEmail: 'contact@niswainfo.com',
      phone: '+91 9876543210',
      address: 'Mumbai, Maharashtra, India',
      timezone: 'Asia/Kolkata',
      language: 'en',
      currency: 'INR'
    },
    email: {
      smtpHost: 'smtp.gmail.com',
      smtpPort: '587',
      smtpUsername: 'noreply@niswainfo.com',
      smtpPassword: '••••••••••••',
      fromName: 'Niswa Info Tech',
      fromEmail: 'noreply@niswainfo.com',
      enableEmailNotifications: true,
      enableContactNotifications: true,
      enableOrderNotifications: true,
      // New email notification toggles
      emailNotificationsEnabled: true,
      emailContactNotificationsEnabled: true,
      emailOrderNotificationsEnabled: true,
      emailNewsletterNotificationsEnabled: true,
      emailPasswordResetEnabled: true,
      emailOrderConfirmationEnabled: true,
      emailOrderStatusUpdateEnabled: true,
      emailPaymentConfirmationEnabled: true,
      emailPaymentReminderEnabled: true,
      emailSplitPaymentReminderEnabled: true,
      emailSplitPaymentWarningEnabled: true,
      emailSplitPaymentFineAlertEnabled: true,
      emailAppointmentConfirmationEnabled: true,
      emailQuoteReadyEnabled: true,
      emailUserNotificationEnabled: true,
      emailAdminNotificationEnabled: true,
      emailSystemAlertEnabled: true,
      emailMaintenanceNotificationEnabled: true,
      // User Account Email Settings
      emailAccountCreationEnabled: true,
      emailLoginAlertEnabled: true,
      emailMultiDeviceWarningEnabled: true,
      // Product and Promotion Email Settings
      emailProductNotificationEnabled: true,
      emailPromotionNotificationEnabled: true,
      // Coupon email notification settings
      emailCouponAssignedNotificationEnabled: true,
      emailCouponCreatedNotificationEnabled: true,
      emailCouponUpdatedNotificationEnabled: true,
      emailCouponExpiryReminderEnabled: true,
      emailCouponUsageNotificationEnabled: true,
      // Email OTP Verification Settings
      emailOtpVerificationEnabled: true
    },
    // Security settings moved to Security Management
    notifications: {
      emailNotifications: true,
      pushNotifications: false,
      smsNotifications: false,
      newUserRegistration: true,
      newContactInquiry: true,
      newProjectSubmission: true,
      paymentReceived: true,
      projectStatusUpdate: true,
      systemAlerts: true
    },
    system: {
      maintenanceMode: false,
      comingSoonMode: false,
      homeMaintenanceMode: false,
      aboutMaintenanceMode: false,
      servicesMaintenanceMode: false,
      productsMaintenanceMode: false,
      portfolioMaintenanceMode: false,
      blogMaintenanceMode: false,
      contactMaintenanceMode: false,
      quoteMaintenanceMode: false,
      appointmentMaintenanceMode: false,
      cartMaintenanceMode: false,
      checkoutMaintenanceMode: false,
      paymentMaintenanceMode: false,
      thankYouMaintenanceMode: false,
      wishlistMaintenanceMode: false,
      privacyPolicyMaintenanceMode: false,
      termsConditionsMaintenanceMode: false,
      refundPolicyMaintenanceMode: false,
      loginMaintenanceMode: false,
      registerMaintenanceMode: false,
      forgotPasswordMaintenanceMode: false,
      resetPasswordMaintenanceMode: false,
      homeComingSoonMode: false,
      aboutComingSoonMode: false,
      servicesComingSoonMode: false,
      productsComingSoonMode: false,
      portfolioComingSoonMode: false,
      blogComingSoonMode: false,
      contactComingSoonMode: false,
      quoteComingSoonMode: false,
      appointmentComingSoonMode: false,
      cartComingSoonMode: false,
      checkoutComingSoonMode: false,
      paymentComingSoonMode: false,
      thankYouComingSoonMode: false,
      wishlistComingSoonMode: false,
      privacyPolicyComingSoonMode: false,
      termsConditionsComingSoonMode: false,
      refundPolicyComingSoonMode: false,
      loginComingSoonMode: false,
      registerComingSoonMode: false,
      forgotPasswordComingSoonMode: false,
      resetPasswordComingSoonMode: false
    },
    integrations: {
      // Firebase Settings
      firebaseEnabled: false,
      firebaseApiKey: '',
      firebaseAuthDomain: '',
      firebaseProjectId: '',
      firebaseStorageBucket: '',
      firebaseMessagingSenderId: '',
      firebaseAppId: '',
      firebaseClientEmail: '',
      firebasePrivateKey: '',
      
      // reCAPTCHA Settings moved to Security Management
      
      // Google Analytics Settings
      googleAnalyticsEnabled: false,
      googleAnalyticsTrackingId: '',
      googleAnalyticsMeasurementId: '',
      
      // Google Maps Settings
      googleMapsEnabled: false,
      googleMapsApiKey: '',
      
      // SMTP Settings moved to Email section
      
      // OneSignal Settings
      onesignalEnabled: false,
      onesignalAppId: '',
      onesignalRestApiKey: '',
      onesignalUserAuthKey: '',
      onesignalSafariWebId: '',
      
      // AI Settings
      aiEnabled: false,
      openaiApiKey: '',
      aiChatModel: 'gpt-3.5-turbo',
      aiContentModel: 'gpt-4',
      aiMaxTokens: '1000',
      aiTemperature: '0.7',
      
      // Legacy settings (keeping for backward compatibility)
      googleAnalyticsId: 'GA-XXXXXXXXX',
      facebookPixelId: 'FB-XXXXXXXXX',
      razorpayKeyId: 'rzp_test_XXXXXXXXX',
      razorpayKeySecret: '••••••••••••',
      openaiApiKey: '••••••••••••',
      enableGoogleAnalytics: true,
      enableFacebookPixel: false,
      enableRazorpay: true,
      enableOpenAI: true
    },
    appearance: {
      theme: 'light',
      primaryColor: '#3B82F6',
      secondaryColor: '#10B981',
      accentColor: '#F59E0B',
      enableDarkMode: true,
      customCSS: '',
      logoLight: '',
      logoDark: '',
      logoLightFile: '',
      logoDarkFile: '',
      favicon: '',
      faviconFile: ''
    }
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [passwordChangeStatus, setPasswordChangeStatus] = useState('');
  const [passwordChangeError, setPasswordChangeError] = useState('');

  const fetchSettings = async () => {
    setLoading(true);
    try {
      console.log('🔍 Fetching settings...');
      const response = await adminAPI.getSettings();
      console.log('🔍 Settings response:', response);
      
      if (response.data && response.data.data && response.data.data.settings) {
        // Flatten settings object for local state
        const backendSettings = response.data.data.settings;
        console.log('🔍 Backend settings:', backendSettings);
        console.log('🔍 Backend settings type:', typeof backendSettings);
        console.log('🔍 Backend settings keys:', Object.keys(backendSettings));
        if (Object.keys(backendSettings).length > 0) {
          const firstKey = Object.keys(backendSettings)[0];
          console.log('🔍 First setting example:', firstKey, backendSettings[firstKey]);
        }
        
        // Ensure backendSettings is an object and has entries
        if (!backendSettings || typeof backendSettings !== 'object' || Object.keys(backendSettings).length === 0) {
          console.warn('⚠️ Backend settings is empty or invalid:', backendSettings);
          return;
        }
        
        const newSettings = {
          general: {}, email: {}, security: {}, notifications: {}, integrations: {}, appearance: {}, system: {}
        };
        
                          // Map database keys to frontend field names
        const dbKeyToField = {
          // Site settings
          'site_title': 'general.siteTitle',
          'site_tagline': 'general.siteTagline',
            // General settings - map to actual frontend field names
            'site_name': 'general.siteName',
            'site_description': 'general.siteDescription',
            'contact_email': 'general.contactEmail',
            'contact_phone': 'general.phone', // Frontend uses 'phone'
            'address': 'general.address',
            
            // Map other database fields to frontend fields
            'admin_email': 'general.adminEmail',
            'site_url': 'general.siteUrl',
            'timezone': 'general.timezone',
            'language': 'general.language',
            'currency': 'general.currency',
            
            // SEO settings (if they exist in frontend)
            'seo_home_title': 'general.seoHomeTitle',
            'seo_home_description': 'general.seoHomeDescription',
            'seo_home_keywords': 'general.seoHomeKeywords',
            'seo_about_title': 'general.seoAboutTitle',
            'seo_about_description': 'general.seoAboutDescription',
            'seo_about_keywords': 'general.seoAboutKeywords',
            'seo_services_title': 'general.seoServicesTitle',
            'seo_services_description': 'general.seoServicesDescription',
            'seo_services_keywords': 'general.seoServicesKeywords',
            'seo_portfolio_title': 'general.seoPortfolioTitle',
            'seo_portfolio_description': 'general.seoPortfolioDescription',
            'seo_portfolio_keywords': 'general.seoPortfolioKeywords',
            'seo_products_title': 'general.seoProductsTitle',
            'seo_products_description': 'general.seoProductsDescription',
            'seo_products_keywords': 'general.seoProductsKeywords',
            'seo_contact_title': 'general.seoContactTitle',
            'seo_contact_description': 'general.seoContactDescription',
            'seo_contact_keywords': 'general.seoContactKeywords',
            
            // Social media settings
            'social_facebook': 'general.socialFacebook',
            'social_twitter': 'general.socialTwitter',
            'social_instagram': 'general.socialInstagram',
            'social_linkedin': 'general.socialLinkedin',
            
            // Appearance settings
            'theme': 'appearance.theme',
            'primary_color': 'appearance.primaryColor',
            'secondary_color': 'appearance.secondaryColor',
            'accent_color': 'appearance.accentColor',
            'enable_dark_mode': 'appearance.enableDarkMode',
            'custom_css': 'appearance.customCSS',
            'logo_light': 'appearance.logoLight',
            'logo_dark': 'appearance.logoDark',
            'logo_light_file': 'appearance.logoLightFile',
            'logo_dark_file': 'appearance.logoDarkFile',
            'favicon': 'appearance.favicon',
            'favicon_file': 'appearance.faviconFile',
            'theme_color': 'appearance.themeColor',
            'background_color': 'appearance.backgroundColor',
            
            // Firebase Settings
            'firebase_enabled': 'integrations.firebaseEnabled',
            'firebase_api_key': 'integrations.firebaseApiKey',
            'firebase_auth_domain': 'integrations.firebaseAuthDomain',
            'firebase_project_id': 'integrations.firebaseProjectId',
            'firebase_storage_bucket': 'integrations.firebaseStorageBucket',
            'firebase_messaging_sender_id': 'integrations.firebaseMessagingSenderId',
            'firebase_app_id': 'integrations.firebaseAppId',
            'firebase_client_email': 'integrations.firebaseClientEmail',
            'firebase_private_key': 'integrations.firebasePrivateKey',
            
            // reCAPTCHA Settings moved to Security Management
            
            // Google Analytics Settings
            'google_analytics_enabled': 'integrations.googleAnalyticsEnabled',
            'google_analytics_tracking_id': 'integrations.googleAnalyticsTrackingId',
            'google_analytics_measurement_id': 'integrations.googleAnalyticsMeasurementId',
            
            // Google Maps Settings
            'google_maps_enabled': 'integrations.googleMapsEnabled',
            'google_maps_api_key': 'integrations.googleMapsApiKey',
            
            // SMTP Settings moved to Email section
            
            // OneSignal Settings
            'onesignal_enabled': 'integrations.onesignalEnabled',
            'onesignal_app_id': 'integrations.onesignalAppId',
            'onesignal_rest_api_key': 'integrations.onesignalRestApiKey',
            'onesignal_user_auth_key': 'integrations.onesignalUserAuthKey',
            'onesignal_safari_web_id': 'integrations.onesignalSafariWebId',
            
            // AI Settings
            'ai_enabled': 'integrations.aiEnabled',
            'openai_api_key': 'integrations.openaiApiKey',
            'ai_chat_model': 'integrations.aiChatModel',
            'ai_content_model': 'integrations.aiContentModel',
            'ai_max_tokens': 'integrations.aiMaxTokens',
            'ai_temperature': 'integrations.aiTemperature',

            // System settings
            'maintenance_mode': 'system.maintenanceMode',
            'coming_soon_mode': 'system.comingSoonMode',
            'home_maintenance_mode': 'system.homeMaintenanceMode',
            'about_maintenance_mode': 'system.aboutMaintenanceMode',
            'services_maintenance_mode': 'system.servicesMaintenanceMode',
            'products_maintenance_mode': 'system.productsMaintenanceMode',
            'portfolio_maintenance_mode': 'system.portfolioMaintenanceMode',
            'blog_maintenance_mode': 'system.blogMaintenanceMode',
            'contact_maintenance_mode': 'system.contactMaintenanceMode',
            'quote_maintenance_mode': 'system.quoteMaintenanceMode',
            'appointment_maintenance_mode': 'system.appointmentMaintenanceMode',
            'cart_maintenance_mode': 'system.cartMaintenanceMode',
            'checkout_maintenance_mode': 'system.checkoutMaintenanceMode',
            'payment_maintenance_mode': 'system.paymentMaintenanceMode',
            'thank_you_maintenance_mode': 'system.thankYouMaintenanceMode',
            'wishlist_maintenance_mode': 'system.wishlistMaintenanceMode',
            'privacy_policy_maintenance_mode': 'system.privacyPolicyMaintenanceMode',
            'terms_conditions_maintenance_mode': 'system.termsConditionsMaintenanceMode',
            'refund_policy_maintenance_mode': 'system.refundPolicyMaintenanceMode',
            'login_maintenance_mode': 'system.loginMaintenanceMode',
            'register_maintenance_mode': 'system.registerMaintenanceMode',
            'forgot_password_maintenance_mode': 'system.forgotPasswordMaintenanceMode',
            'reset_password_maintenance_mode': 'system.resetPasswordMaintenanceMode',
            'home_coming_soon_mode': 'system.homeComingSoonMode',
            'about_coming_soon_mode': 'system.aboutComingSoonMode',
            'services_coming_soon_mode': 'system.servicesComingSoonMode',
            'products_coming_soon_mode': 'system.productsComingSoonMode',
            'portfolio_coming_soon_mode': 'system.portfolioComingSoonMode',
            'blog_coming_soon_mode': 'system.blogComingSoonMode',
            'contact_coming_soon_mode': 'system.contactComingSoonMode',
            'quote_coming_soon_mode': 'system.quoteComingSoonMode',
            'appointment_coming_soon_mode': 'system.appointmentComingSoonMode',
            'cart_coming_soon_mode': 'system.cartComingSoonMode',
            'checkout_coming_soon_mode': 'system.checkoutComingSoonMode',
            'payment_coming_soon_mode': 'system.paymentComingSoonMode',
            'thank_you_coming_soon_mode': 'system.thankYouComingSoonMode',
            'wishlist_coming_soon_mode': 'system.wishlistComingSoonMode',
            'privacy_policy_coming_soon_mode': 'system.privacyPolicyComingSoonMode',
            'terms_conditions_coming_soon_mode': 'system.termsConditionsComingSoonMode',
            'refund_policy_coming_soon_mode': 'system.refundPolicyComingSoonMode',
            'login_coming_soon_mode': 'system.loginComingSoonMode',
            'register_coming_soon_mode': 'system.registerComingSoonMode',
            'forgot_password_coming_soon_mode': 'system.forgotPasswordComingSoonMode',
            'reset_password_coming_soon_mode': 'system.resetPasswordComingSoonMode',
            
            // Email notification settings
            'email_notifications_enabled': 'email.emailNotificationsEnabled',
            'email_contact_notifications_enabled': 'email.emailContactNotificationsEnabled',
            'email_order_notifications_enabled': 'email.emailOrderNotificationsEnabled',
            'email_newsletter_notifications_enabled': 'email.emailNewsletterNotificationsEnabled',
            'email_password_reset_enabled': 'email.emailPasswordResetEnabled',
            'email_order_confirmation_enabled': 'email.emailOrderConfirmationEnabled',
            'email_order_status_update_enabled': 'email.emailOrderStatusUpdateEnabled',
            'email_payment_confirmation_enabled': 'email.emailPaymentConfirmationEnabled',
            'email_payment_reminder_enabled': 'email.emailPaymentReminderEnabled',
            'email_split_payment_reminder_enabled': 'email.emailSplitPaymentReminderEnabled',
            'email_split_payment_warning_enabled': 'email.emailSplitPaymentWarningEnabled',
            'email_split_payment_fine_alert_enabled': 'email.emailSplitPaymentFineAlertEnabled',
            'email_appointment_confirmation_enabled': 'email.emailAppointmentConfirmationEnabled',
            'email_quote_ready_enabled': 'email.emailQuoteReadyEnabled',
            'email_user_notification_enabled': 'email.emailUserNotificationEnabled',
            'email_admin_notification_enabled': 'email.emailAdminNotificationEnabled',
            'email_system_alert_enabled': 'email.emailSystemAlertEnabled',
            'email_maintenance_notification_enabled': 'email.emailMaintenanceNotificationEnabled',
            'email_account_creation_enabled': 'email.emailAccountCreationEnabled',
            'email_login_alert_enabled': 'email.emailLoginAlertEnabled',
            'email_multi_device_warning_enabled': 'email.emailMultiDeviceWarningEnabled',
                  'email_product_notification_enabled': 'email.emailProductNotificationEnabled',
      'email_promotion_notification_enabled': 'email.emailPromotionNotificationEnabled',
      // Coupon email notification settings
      'email.emailCouponAssignedNotificationEnabled': 'coupon_assigned_notification',
      'email.emailCouponCreatedNotificationEnabled': 'coupon_created_notification',
      'email.emailCouponUpdatedNotificationEnabled': 'coupon_updated_notification',
      'email.emailCouponExpiryReminderEnabled': 'coupon_expiry_reminder',
      'email.emailCouponUsageNotificationEnabled': 'coupon_usage_notification',
      // Coupon email notification settings
      'coupon_assigned_notification': 'email.emailCouponAssignedNotificationEnabled',
      'coupon_created_notification': 'email.emailCouponCreatedNotificationEnabled',
      'coupon_updated_notification': 'email.emailCouponUpdatedNotificationEnabled',
      'coupon_expiry_reminder': 'email.emailCouponExpiryReminderEnabled',
      'coupon_usage_notification': 'email.emailCouponUsageNotificationEnabled',
            'email_otp_verification_enabled': 'email.emailOtpVerificationEnabled'
          };
        
        // Map backend keys to frontend structure
        Object.entries(backendSettings).forEach(([key, obj]) => {
          try {
            console.log(`🔍 Processing key: ${key}, obj:`, obj);
            
            // Ensure key and obj are valid
            if (!key || obj === undefined || obj === null) {
              console.warn(`⚠️ Invalid key or obj for: ${key}`, obj);
              return;
            }
            
            const fieldPath = dbKeyToField[key];
            if (fieldPath && obj) {
              const [category, fieldName] = fieldPath.split('.');
              
              // Ensure category and fieldName are valid
              if (!category || !fieldName) {
                console.warn(`⚠️ Invalid fieldPath: ${fieldPath} for key: ${key}`);
                return;
              }
              
              // Handle different possible object structures
              let value = '';
              if (obj && typeof obj === 'object' && obj.value !== undefined) {
                value = obj.value;
              } else if (obj && typeof obj === 'object' && obj.setting_value !== undefined) {
                value = obj.setting_value;
              } else if (typeof obj === 'string') {
                value = obj;
              } else if (obj && typeof obj === 'object') {
                // Try to get the first property if it's an object
                const firstProp = Object.values(obj)[0];
                value = firstProp || '';
              } else {
                console.warn(`⚠️ Unknown object structure for key: ${key}`, obj);
                value = '';
              }
              
              newSettings[category][fieldName] = value || '';
            } else {
              console.warn(`⚠️ No frontend mapping found for DB key: ${key} or obj is undefined`);
            }
          } catch (error) {
            console.error(`❌ Error processing key: ${key}`, error);
          }
        });
        console.log('🔍 Mapped settings:', newSettings);
        
        // Debug: Check if page-level settings are being loaded
        console.log('🔍 Page-level settings check:', {
          homeMaintenanceMode: newSettings.system?.homeMaintenanceMode,
          homeComingSoonMode: newSettings.system?.homeComingSoonMode,
          aboutMaintenanceMode: newSettings.system?.aboutMaintenanceMode,
          aboutComingSoonMode: newSettings.system?.aboutComingSoonMode,
          allSystemSettings: newSettings.system
        });
        
        // Merge with defaults to ensure all fields exist
        const mergedSettings = {
          general: { ...settings.general, ...newSettings.general },
          email: { ...settings.email, ...newSettings.email },
          security: { ...settings.security, ...newSettings.security },
          notifications: { ...settings.notifications, ...newSettings.notifications },
          integrations: { ...settings.integrations, ...newSettings.integrations },
          appearance: { ...settings.appearance, ...newSettings.appearance },
          system: { ...settings.system, ...newSettings.system }
        };
        
        console.log('🔍 Final merged settings:', {
          systemSettings: mergedSettings.system,
          pageLevelSettings: Object.keys(mergedSettings.system).filter(key => 
            key.includes('MaintenanceMode') || key.includes('ComingSoonMode')
          )
        });
        
        setSettings(mergedSettings);
      } else {
        console.log('🔍 No settings found, using defaults');
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSettingChange = (category, key) => (e) => {
    const value = e.target.value;
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  // Custom handler for system mode settings to implement mutual exclusivity
  const handleSystemModeChange = (mode) => (e) => {
    const isEnabled = e.target.value;
    
    setSettings(prev => ({
      ...prev,
      system: {
        ...prev.system,
        // If the mode being toggled is being enabled, disable the other mode
        maintenanceMode: mode === 'maintenance' ? isEnabled : (isEnabled ? false : prev.system.maintenanceMode),
        comingSoonMode: mode === 'comingSoon' ? isEnabled : (isEnabled ? false : prev.system.comingSoonMode)
      }
    }));
  };

  // New handler for individual page mode settings
  const handlePageModeChange = (pageKey, mode) => (e) => {
    const isEnabled = e.target.value;
    
    // Convert mode to proper format (maintenance -> MaintenanceMode, comingSoon -> ComingSoonMode)
    const modeKey = mode === 'maintenance' ? 'MaintenanceMode' : 'ComingSoonMode';
    const settingKey = `${pageKey}${modeKey}`;
    
    console.log('🔍 Page mode change:', {
      pageKey,
      mode,
      modeKey,
      settingKey,
      isEnabled,
      currentValue: settings.system[settingKey]
    });
    
    setSettings(prev => {
      const newSettings = {
        ...prev,
        system: {
          ...prev.system,
          // If the mode being toggled is being enabled, disable the other mode for the same page
          [settingKey]: isEnabled,
          // Disable the other mode for this page when one is enabled
          [`${pageKey}${mode === 'maintenance' ? 'ComingSoonMode' : 'MaintenanceMode'}`]: isEnabled ? false : prev.system[`${pageKey}${mode === 'maintenance' ? 'ComingSoonMode' : 'MaintenanceMode'}`]
        }
      };
      
      console.log('🔍 Updated settings:', {
        oldValue: prev.system[settingKey],
        newValue: newSettings.system[settingKey],
        otherModeOldValue: prev.system[`${pageKey}${mode === 'maintenance' ? 'ComingSoonMode' : 'MaintenanceMode'}`],
        otherModeNewValue: newSettings.system[`${pageKey}${mode === 'maintenance' ? 'ComingSoonMode' : 'MaintenanceMode'}`],
        allSystemSettings: newSettings.system
      });
      
      return newSettings;
    });
  };

  const handleSave = async (category) => {
    setLoading(true);
    setSaveMessage('');
    setSaveStatus('');
    
    try {
      // Map frontend field names to database keys
      const fieldToDbKey = {
        // Site settings
        'general.siteTitle': 'site_title',
        'general.siteTagline': 'site_tagline',
        // General settings - map to actual database field names
        'general.siteName': 'site_name',
        'general.siteDescription': 'site_description',
        'general.contactEmail': 'contact_email',
        'general.phone': 'contact_phone', // Frontend uses 'phone', DB uses 'contact_phone'
        'general.address': 'address',
        'general.adminEmail': 'admin_email',
        'general.siteUrl': 'site_url',
        'general.timezone': 'timezone',
        'general.language': 'language',
        'general.currency': 'currency',
        
        // SEO settings (if they exist in database)
        'general.seoHomeTitle': 'seo_home_title',
        'general.seoHomeDescription': 'seo_home_description',
        'general.seoHomeKeywords': 'seo_home_keywords',
        'general.seoAboutTitle': 'seo_about_title',
        'general.seoAboutDescription': 'seo_about_description',
        'general.seoAboutKeywords': 'seo_about_keywords',
        'general.seoServicesTitle': 'seo_services_title',
        'general.seoServicesDescription': 'seo_services_description',
        'general.seoServicesKeywords': 'seo_services_keywords',
        'general.seoPortfolioTitle': 'seo_portfolio_title',
        'general.seoPortfolioDescription': 'seo_portfolio_description',
        'general.seoPortfolioKeywords': 'seo_portfolio_keywords',
        'general.seoProductsTitle': 'seo_products_title',
        'general.seoProductsDescription': 'seo_products_description',
        'general.seoProductsKeywords': 'seo_products_keywords',
        'general.seoContactTitle': 'seo_contact_title',
        'general.seoContactDescription': 'seo_contact_description',
        'general.seoContactKeywords': 'seo_contact_keywords',
        
        // Social media settings
        'general.socialFacebook': 'social_facebook',
        'general.socialTwitter': 'social_twitter',
        'general.socialInstagram': 'social_instagram',
        'general.socialLinkedin': 'social_linkedin',
        
        // Appearance settings
        'appearance.theme': 'theme',
        'appearance.primaryColor': 'primary_color',
        'appearance.secondaryColor': 'secondary_color',
        'appearance.accentColor': 'accent_color',
        'appearance.enableDarkMode': 'enable_dark_mode',
        'appearance.customCSS': 'custom_css',
        'appearance.logoLight': 'logo_light',
        'appearance.logoDark': 'logo_dark',
        'appearance.logoLightFile': 'logo_light_file',
        'appearance.logoDarkFile': 'logo_dark_file',
        'appearance.favicon': 'favicon',
        'appearance.faviconFile': 'favicon_file',
        'appearance.themeColor': 'theme_color',
        'appearance.backgroundColor': 'background_color',
        
        // Firebase Settings
        'integrations.firebaseEnabled': 'firebase_enabled',
        'integrations.firebaseApiKey': 'firebase_api_key',
        'integrations.firebaseAuthDomain': 'firebase_auth_domain',
        'integrations.firebaseProjectId': 'firebase_project_id',
        'integrations.firebaseStorageBucket': 'firebase_storage_bucket',
        'integrations.firebaseMessagingSenderId': 'firebase_messaging_sender_id',
        'integrations.firebaseAppId': 'firebase_app_id',
        'integrations.firebaseClientEmail': 'firebase_client_email',
        'integrations.firebasePrivateKey': 'firebase_private_key',
        
        // reCAPTCHA Settings moved to Security Management
        
        // Google Analytics Settings
        'integrations.googleAnalyticsEnabled': 'google_analytics_enabled',
        'integrations.googleAnalyticsTrackingId': 'google_analytics_tracking_id',
        'integrations.googleAnalyticsMeasurementId': 'google_analytics_measurement_id',
        
        // Google Maps Settings
        'integrations.googleMapsEnabled': 'google_maps_enabled',
        'integrations.googleMapsApiKey': 'google_maps_api_key',
        
        // SMTP Settings moved to Email section
        
        // OneSignal Settings
        'integrations.onesignalEnabled': 'onesignal_enabled',
        'integrations.onesignalAppId': 'onesignal_app_id',
        'integrations.onesignalRestApiKey': 'onesignal_rest_api_key',
              'integrations.onesignalUserAuthKey': 'onesignal_user_auth_key',
      'integrations.onesignalSafariWebId': 'onesignal_safari_web_id',
      
      // AI Settings
      'integrations.aiEnabled': 'ai_enabled',
      'integrations.openaiApiKey': 'openai_api_key',
      'integrations.aiChatModel': 'ai_chat_model',
      'integrations.aiContentModel': 'ai_content_model',
      'integrations.aiMaxTokens': 'ai_max_tokens',
      'integrations.aiTemperature': 'ai_temperature',

        // System settings
        'system.maintenanceMode': 'maintenance_mode',
        'system.comingSoonMode': 'coming_soon_mode',
        'system.homeMaintenanceMode': 'home_maintenance_mode',
        'system.aboutMaintenanceMode': 'about_maintenance_mode',
        'system.servicesMaintenanceMode': 'services_maintenance_mode',
        'system.productsMaintenanceMode': 'products_maintenance_mode',
        'system.portfolioMaintenanceMode': 'portfolio_maintenance_mode',
        'system.blogMaintenanceMode': 'blog_maintenance_mode',
        'system.contactMaintenanceMode': 'contact_maintenance_mode',
        'system.quoteMaintenanceMode': 'quote_maintenance_mode',
        'system.appointmentMaintenanceMode': 'appointment_maintenance_mode',
        'system.cartMaintenanceMode': 'cart_maintenance_mode',
        'system.checkoutMaintenanceMode': 'checkout_maintenance_mode',
        'system.paymentMaintenanceMode': 'payment_maintenance_mode',
        'system.thankYouMaintenanceMode': 'thank_you_maintenance_mode',
        'system.wishlistMaintenanceMode': 'wishlist_maintenance_mode',
        'system.privacyPolicyMaintenanceMode': 'privacy_policy_maintenance_mode',
        'system.termsConditionsMaintenanceMode': 'terms_conditions_maintenance_mode',
        'system.refundPolicyMaintenanceMode': 'refund_policy_maintenance_mode',
        'system.loginMaintenanceMode': 'login_maintenance_mode',
        'system.registerMaintenanceMode': 'register_maintenance_mode',
        'system.forgotPasswordMaintenanceMode': 'forgot_password_maintenance_mode',
        'system.resetPasswordMaintenanceMode': 'reset_password_maintenance_mode',
        'system.homeComingSoonMode': 'home_coming_soon_mode',
        'system.aboutComingSoonMode': 'about_coming_soon_mode',
        'system.servicesComingSoonMode': 'services_coming_soon_mode',
        'system.productsComingSoonMode': 'products_coming_soon_mode',
        'system.portfolioComingSoonMode': 'portfolio_coming_soon_mode',
        'system.blogComingSoonMode': 'blog_coming_soon_mode',
        'system.contactComingSoonMode': 'contact_coming_soon_mode',
        'system.quoteComingSoonMode': 'quote_coming_soon_mode',
        'system.appointmentComingSoonMode': 'appointment_coming_soon_mode',
        'system.cartComingSoonMode': 'cart_coming_soon_mode',
        'system.checkoutComingSoonMode': 'checkout_coming_soon_mode',
        'system.paymentComingSoonMode': 'payment_coming_soon_mode',
        'system.thankYouComingSoonMode': 'thank_you_coming_soon_mode',
        'system.wishlistComingSoonMode': 'wishlist_coming_soon_mode',
        'system.privacyPolicyComingSoonMode': 'privacy_policy_coming_soon_mode',
        'system.termsConditionsComingSoonMode': 'terms_conditions_coming_soon_mode',
        'system.refundPolicyComingSoonMode': 'refund_policy_coming_soon_mode',
        'system.loginComingSoonMode': 'login_coming_soon_mode',
        'system.registerComingSoonMode': 'register_coming_soon_mode',
        'system.forgotPasswordComingSoonMode': 'forgot_password_coming_soon_mode',
        'system.resetPasswordComingSoonMode': 'reset_password_coming_soon_mode',
        
        // Email notification settings
        'email.emailNotificationsEnabled': 'email_notifications_enabled',
        'email.emailContactNotificationsEnabled': 'email_contact_notifications_enabled',
        'email.emailOrderNotificationsEnabled': 'email_order_notifications_enabled',
        'email.emailNewsletterNotificationsEnabled': 'email_newsletter_notifications_enabled',
        'email.emailPasswordResetEnabled': 'email_password_reset_enabled',
        'email.emailOrderConfirmationEnabled': 'email_order_confirmation_enabled',
        'email.emailOrderStatusUpdateEnabled': 'email_order_status_update_enabled',
        'email.emailPaymentConfirmationEnabled': 'email_payment_confirmation_enabled',
        'email.emailPaymentReminderEnabled': 'email_payment_reminder_enabled',
        'email.emailSplitPaymentReminderEnabled': 'email_split_payment_reminder_enabled',
        'email.emailSplitPaymentWarningEnabled': 'email_split_payment_warning_enabled',
        'email.emailSplitPaymentFineAlertEnabled': 'email_split_payment_fine_alert_enabled',
        'email.emailAppointmentConfirmationEnabled': 'email_appointment_confirmation_enabled',
        'email.emailQuoteReadyEnabled': 'email_quote_ready_enabled',
        'email.emailUserNotificationEnabled': 'email_user_notification_enabled',
        'email.emailAdminNotificationEnabled': 'email_admin_notification_enabled',
        'email.emailSystemAlertEnabled': 'email_system_alert_enabled',
        'email.emailMaintenanceNotificationEnabled': 'email_maintenance_notification_enabled',
        // User Account Email Settings
        'email.emailAccountCreationEnabled': 'email_account_creation_enabled',
        'email.emailLoginAlertEnabled': 'email_login_alert_enabled',
        'email.emailMultiDeviceWarningEnabled': 'email_multi_device_warning_enabled',
        // Product and Promotion Email Settings
        'email.emailProductNotificationEnabled': 'email_product_notification_enabled',
        'email.emailPromotionNotificationEnabled': 'email_promotion_notification_enabled',
        // Email OTP Verification Settings
        'email.emailOtpVerificationEnabled': 'email_otp_verification_enabled'
      };
      
      // Flatten settings for backend using proper database keys
      const flatSettings = {};
      Object.entries(settings).forEach(([cat, values]) => {
        Object.entries(values).forEach(([key, value]) => {
          const frontendKey = `${cat}.${key}`;
          const dbKey = fieldToDbKey[frontendKey];
          if (dbKey) {
            flatSettings[dbKey] = value;
          } else {
            console.warn(`⚠️ No database mapping found for: ${frontendKey}`);
          }
        });
      });
      
      console.log('🔍 Saving settings with DB keys:', flatSettings);
      
      const response = await adminAPI.updateSettings(flatSettings);
      
      console.log('🔍 Save response:', response);
      
      if (response.data.success) {
        setSaveMessage('Settings saved successfully!');
        setSaveStatus('success');
        // Refresh settings after save without page reload
        setTimeout(() => {
          fetchSettings();
        }, 1000);
      } else {
        setSaveMessage('Failed to save settings. Please try again.');
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage('Error saving settings. Please check your connection and try again.');
      setSaveStatus('error');
    } finally {
      setLoading(false);
      // Clear message after 3 seconds
      setTimeout(() => {
        setSaveMessage('');
        setSaveStatus('');
      }, 3000);
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: Globe },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'system', label: 'System', icon: Server },
    { id: 'integrations', label: 'Integrations', icon: Key },
    { id: 'appearance', label: 'Appearance', icon: Palette }
  ];



  const ToggleField = ({ label, value, onChange, description }) => (
    <div className="mb-4">
      <div className="flex items-center justify-between">
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {label}
          </label>
          {description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{description}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            // Create a mock event object with target.value for the handleSettingChange function
            const mockEvent = { target: { value: !value } };
            onChange(mockEvent);
          }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            value ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              value ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          label="Site Title"
          value={settings.general.siteTitle}
          onChange={handleSettingChange('general', 'siteTitle')}
          placeholder="Website title displayed in browser and header"
          required
        />
        <InputField
          label="Site Tagline"
          value={settings.general.siteTagline}
          onChange={handleSettingChange('general', 'siteTagline')}
          placeholder="Brief tagline/description for your website"
        />
      </div>
      
      {/* Site Name, Site URL, and Site Description hidden - removed as requested */}
      {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          label="Site Name"
          value={settings.general.siteName}
          onChange={handleSettingChange('general', 'siteName')}
          required
        />
        <InputField
          label="Site URL"
          value={settings.general.siteUrl}
          onChange={handleSettingChange('general', 'siteUrl')}
          required
        />
      </div>
      
      <TextareaField
        label="Site Description"
        value={settings.general.siteDescription}
        onChange={handleSettingChange('general', 'siteDescription')}
        placeholder="Brief description of your website"
      /> */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          label="Admin Email"
          type="email"
          value={settings.general.adminEmail}
          onChange={handleSettingChange('general', 'adminEmail')}
          required
        />
        <InputField
          label="Contact Email"
          type="email"
          value={settings.general.contactEmail}
          onChange={handleSettingChange('general', 'contactEmail')}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          label="Phone Number"
          value={settings.general.phone}
          onChange={handleSettingChange('general', 'phone')}
        />
        <SelectField
          label="Timezone"
          value={settings.general.timezone}
          onChange={handleSettingChange('general', 'timezone')}
          options={[
            { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
            { value: 'America/New_York', label: 'America/New_York (EST)' },
            { value: 'Europe/London', label: 'Europe/London (GMT)' },
            { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' }
          ]}
        />
      </div>

      <TextareaField
        label="Business Address"
        value={settings.general.address}
        onChange={handleSettingChange('general', 'address')}
        placeholder="Your business address"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SelectField
          label="Default Language"
          value={settings.general.language}
          onChange={handleSettingChange('general', 'language')}
          options={[
            { value: 'en', label: 'English' },
            { value: 'hi', label: 'Hindi' },
            { value: 'es', label: 'Spanish' },
            { value: 'fr', label: 'French' }
          ]}
        />
        <SelectField
          label="Default Currency"
          value={settings.general.currency}
          onChange={handleSettingChange('general', 'currency')}
          options={[
            { value: 'INR', label: 'Indian Rupee (₹)' },
            { value: 'USD', label: 'US Dollar ($)' },
            { value: 'EUR', label: 'Euro (€)' },
            { value: 'GBP', label: 'British Pound (£)' }
          ]}
        />
      </div>
    </div>
  );

  const renderEmailSettings = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
        <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Email Notifications</h4>
        <p className="text-sm text-blue-700 dark:text-blue-300">
          Configure email notification preferences for various events. Toggle individual email types on/off as needed.
        </p>
      </div>

      {/* Master Email Toggle */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">Master Settings</h4>
        <div className="space-y-4">
          <ToggleField
            label="Master Email Notifications"
            value={settings.email.emailNotificationsEnabled}
            onChange={handleSettingChange('email', 'emailNotificationsEnabled')}
            description="Master toggle for all email notifications"
          />
        </div>
      </div>

      {/* General Email Notifications */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">General Notifications</h4>
        <div className="space-y-4">
          <ToggleField
            label="Contact Form Notifications"
            value={settings.email.emailContactNotificationsEnabled}
            onChange={handleSettingChange('email', 'emailContactNotificationsEnabled')}
            description="Notify when new contact inquiries are received"
          />
          <ToggleField
            label="Newsletter Notifications"
            value={settings.email.emailNewsletterNotificationsEnabled}
            onChange={handleSettingChange('email', 'emailNewsletterNotificationsEnabled')}
            description="Enable newsletter email sending"
          />
          <ToggleField
            label="Password Reset Emails"
            value={settings.email.emailPasswordResetEnabled}
            onChange={handleSettingChange('email', 'emailPasswordResetEnabled')}
            description="Send password reset emails to users"
          />
        </div>
      </div>

      {/* Order and Payment Notifications */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">Order & Payment Notifications</h4>
        <div className="space-y-4">
          <ToggleField
            label="Order Confirmation Emails"
            value={settings.email.emailOrderConfirmationEnabled}
            onChange={handleSettingChange('email', 'emailOrderConfirmationEnabled')}
            description="Send order confirmation emails to customers"
          />
          <ToggleField
            label="Order Status Update Emails"
            value={settings.email.emailOrderStatusUpdateEnabled}
            onChange={handleSettingChange('email', 'emailOrderStatusUpdateEnabled')}
            description="Notify customers when order status changes"
          />
          <ToggleField
            label="Payment Confirmation Emails"
            value={settings.email.emailPaymentConfirmationEnabled}
            onChange={handleSettingChange('email', 'emailPaymentConfirmationEnabled')}
            description="Send payment confirmation emails"
          />
          <ToggleField
            label="Payment Reminder Emails"
            value={settings.email.emailPaymentReminderEnabled}
            onChange={handleSettingChange('email', 'emailPaymentReminderEnabled')}
            description="Send payment reminder emails"
          />
        </div>
      </div>

      {/* Split Payment Notifications */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">Split Payment Notifications</h4>
        <div className="space-y-4">
          <ToggleField
            label="Split Payment Reminder Emails"
            value={settings.email.emailSplitPaymentReminderEnabled}
            onChange={handleSettingChange('email', 'emailSplitPaymentReminderEnabled')}
            description="Send reminder emails 5 days before due date"
          />
          <ToggleField
            label="Split Payment Warning Emails"
            value={settings.email.emailSplitPaymentWarningEnabled}
            onChange={handleSettingChange('email', 'emailSplitPaymentWarningEnabled')}
            description="Send warning emails from due date to expiry"
          />
          <ToggleField
            label="Split Payment Fine Alert Emails"
            value={settings.email.emailSplitPaymentFineAlertEnabled}
            onChange={handleSettingChange('email', 'emailSplitPaymentFineAlertEnabled')}
            description="Send fine alert emails after expiry date"
          />
        </div>
      </div>

      {/* Appointment and Quote Notifications */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">Appointment & Quote Notifications</h4>
        <div className="space-y-4">
          <ToggleField
            label="Appointment Confirmation Emails"
            value={settings.email.emailAppointmentConfirmationEnabled}
            onChange={handleSettingChange('email', 'emailAppointmentConfirmationEnabled')}
            description="Send appointment confirmation emails"
          />
          <ToggleField
            label="Quote Ready Notifications"
            value={settings.email.emailQuoteReadyEnabled}
            onChange={handleSettingChange('email', 'emailQuoteReadyEnabled')}
            description="Notify when quotes are ready"
          />
        </div>
      </div>

      {/* User and Admin Notifications */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">User & Admin Notifications</h4>
        <div className="space-y-4">
          <ToggleField
            label="User Notification Emails"
            value={settings.email.emailUserNotificationEnabled}
            onChange={handleSettingChange('email', 'emailUserNotificationEnabled')}
            description="Send general user notification emails"
          />
          <ToggleField
            label="Admin Notification Emails"
            value={settings.email.emailAdminNotificationEnabled}
            onChange={handleSettingChange('email', 'emailAdminNotificationEnabled')}
            description="Send admin notification emails"
          />
        </div>
      </div>

      {/* System Notifications */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">System Notifications</h4>
        <div className="space-y-4">
          <ToggleField
            label="System Alert Emails"
            value={settings.email.emailSystemAlertEnabled}
            onChange={handleSettingChange('email', 'emailSystemAlertEnabled')}
            description="Send system alert emails"
          />
          <ToggleField
            label="Maintenance Notification Emails"
            value={settings.email.emailMaintenanceNotificationEnabled}
            onChange={handleSettingChange('email', 'emailMaintenanceNotificationEnabled')}
            description="Send maintenance notification emails"
          />
        </div>
      </div>

      {/* User Account Notifications */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">User Account Notifications</h4>
        <div className="space-y-4">
          <ToggleField
            label="Account Creation Emails"
            value={settings.email.emailAccountCreationEnabled}
            onChange={handleSettingChange('email', 'emailAccountCreationEnabled')}
            description="Send welcome emails to new users and notifications to admin"
          />
          <ToggleField
            label="Login Alert Emails"
            value={settings.email.emailLoginAlertEnabled}
            onChange={handleSettingChange('email', 'emailLoginAlertEnabled')}
            description="Send login alert emails to users"
          />
          <ToggleField
            label="Multi-Device Warning Emails"
            value={settings.email.emailMultiDeviceWarningEnabled}
            onChange={handleSettingChange('email', 'emailMultiDeviceWarningEnabled')}
            description="Send warning emails when users log in from multiple devices"
          />
        </div>
      </div>

      {/* Product and Promotion Notifications */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">Product & Promotion Notifications</h4>
        <div className="space-y-4">
          <ToggleField
            label="Product Notification Emails"
            value={settings.email.emailProductNotificationEnabled}
            onChange={handleSettingChange('email', 'emailProductNotificationEnabled')}
            description="Send product notifications to all users and newsletter subscribers"
          />
          <ToggleField
            label="Promotion Notification Emails"
            value={settings.email.emailPromotionNotificationEnabled}
            onChange={handleSettingChange('email', 'emailPromotionNotificationEnabled')}
            description="Send promotion notifications to all users and newsletter subscribers"
          />
        </div>
      </div>

      {/* Coupon Email Notifications */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">Coupon Email Notifications</h4>
        <div className="space-y-4">
          <ToggleField
            label="Coupon Assignment Notifications"
            value={settings.email.emailCouponAssignedNotificationEnabled}
            onChange={handleSettingChange('email', 'emailCouponAssignedNotificationEnabled')}
            description="Send email when a coupon is assigned to a specific user"
          />
          
          <ToggleField
            label="Coupon Creation Notifications"
            value={settings.email.emailCouponCreatedNotificationEnabled}
            onChange={handleSettingChange('email', 'emailCouponCreatedNotificationEnabled')}
            description="Send email when a new coupon is created and applicable to users"
          />
          
          <ToggleField
            label="Coupon Update Notifications"
            value={settings.email.emailCouponUpdatedNotificationEnabled}
            onChange={handleSettingChange('email', 'emailCouponUpdatedNotificationEnabled')}
            description="Send email when a coupon is updated and affects users"
          />
          
          <ToggleField
            label="Coupon Expiry Reminders"
            value={settings.email.emailCouponExpiryReminderEnabled}
            onChange={handleSettingChange('email', 'emailCouponExpiryReminderEnabled')}
            description="Send reminder emails before coupons expire"
          />
          
          <ToggleField
            label="Coupon Usage Notifications"
            value={settings.email.emailCouponUsageNotificationEnabled}
            onChange={handleSettingChange('email', 'emailCouponUsageNotificationEnabled')}
            description="Send notification when a coupon is used"
          />
        </div>
      </div>

      {/* Email OTP Verification Settings */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">Email OTP Verification</h4>
        <div className="space-y-4">
          <ToggleField
            label="Email OTP Verification"
            value={settings.email.emailOtpVerificationEnabled}
            onChange={handleSettingChange('email', 'emailOtpVerificationEnabled')}
            description="Require email OTP verification for account registration"
          />
        </div>
      </div>

      {/* Legacy Settings (for backward compatibility) */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">Legacy Settings</h4>
        <div className="space-y-4">
          <ToggleField
            label="Enable Email Notifications (Legacy)"
            value={settings.email.enableEmailNotifications}
            onChange={handleSettingChange('email', 'enableEmailNotifications')}
            description="Legacy setting - use Master Email Notifications above"
          />
          <ToggleField
            label="Contact Form Notifications (Legacy)"
            value={settings.email.enableContactNotifications}
            onChange={handleSettingChange('email', 'enableContactNotifications')}
            description="Legacy setting - use Contact Form Notifications above"
          />
          <ToggleField
            label="Order Notifications (Legacy)"
            value={settings.email.enableOrderNotifications}
            onChange={handleSettingChange('email', 'enableOrderNotifications')}
            description="Legacy setting - use Order & Payment Notifications above"
          />
        </div>
      </div>

      {/* SMTP Configuration moved to dedicated Email section */}
    </div>
  );

  // Security settings moved to Security Management
  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
        <h4 className="font-medium text-red-900 dark:text-red-100 mb-2 flex items-center">
          <AlertTriangle className="h-4 w-4 mr-2" />
          Security Settings
        </h4>
        <p className="text-sm text-red-700 dark:text-red-300">
          These settings affect the security of your application. Please configure carefully.
        </p>
      </div>

      <div className="space-y-4">
        <ToggleField
          label="Enable Two-Factor Authentication"
          value={settings.security.enableTwoFactor}
          onChange={handleSettingChange('security', 'enableTwoFactor')}
          description="Require 2FA for admin accounts"
        />
        <ToggleField
          label="Enable Rate Limiting"
          value={settings.security.enableRateLimiting}
          onChange={handleSettingChange('security', 'enableRateLimiting')}
          description="Limit API requests to prevent abuse"
        />
        <ToggleField
          label="Enable CAPTCHA"
          value={settings.security.enableCaptcha}
          onChange={handleSettingChange('security', 'enableCaptcha')}
          description="Show CAPTCHA on login and contact forms"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          label="Session Timeout (minutes)"
          type="number"
          value={settings.security.sessionTimeout}
          onChange={handleSettingChange('security', 'sessionTimeout')}
          placeholder="30"
        />
        <InputField
          label="Max Login Attempts"
          type="number"
          value={settings.security.maxLoginAttempts}
          onChange={handleSettingChange('security', 'maxLoginAttempts')}
          placeholder="5"
        />
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">Password Policy</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField
            label="Minimum Password Length"
            type="number"
            value={settings.security.passwordMinLength}
            onChange={handleSettingChange('security', 'passwordMinLength')}
            placeholder="8"
          />
          <div className="flex items-center mt-8">
            <ToggleField
              label="Require Special Characters"
              value={settings.security.requireSpecialChars}
              onChange={handleSettingChange('security', 'requireSpecialChars')}
            />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">File Upload Security</h4>
        <InputField
          label="Allowed File Types"
          value={settings.security.allowedFileTypes}
          onChange={handleSettingChange('security', 'allowedFileTypes')}
          placeholder="jpg,jpeg,png,gif,pdf,doc,docx"
        />
        <InputField
          label="Maximum File Size (MB)"
          type="number"
          value={settings.security.maxFileSize}
          onChange={handleSettingChange('security', 'maxFileSize')}
          placeholder="10"
        />
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">Change Admin Password</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField
            label="Current Password"
            type="password"
            value={passwordForm.currentPassword}
            onChange={v => setPasswordForm(f => ({ ...f, currentPassword: v }))}
            required
          />
          <InputField
            label="New Password"
            type="password"
            value={passwordForm.newPassword}
            onChange={v => setPasswordForm(f => ({ ...f, newPassword: v }))}
            required
          />
          <InputField
            label="Confirm New Password"
            type="password"
            value={passwordForm.confirmNewPassword}
            onChange={v => setPasswordForm(f => ({ ...f, confirmNewPassword: v }))}
            required
          />
        </div>
        <button
          className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          onClick={async () => {
            setPasswordChangeStatus('');
            setPasswordChangeError('');
            if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmNewPassword) {
              setPasswordChangeError('All fields are required.');
              return;
            }
            if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
              setPasswordChangeError('New passwords do not match.');
              return;
            }
            try {
              await adminAPI.changePassword({
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword
              });
              setPasswordChangeStatus('Password changed successfully.');
              setPasswordForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
            } catch (err) {
              setPasswordChangeError(err?.response?.data?.message || 'Failed to change password.');
            }
          }}
        >
          Change Password
        </button>
        {passwordChangeStatus && <div className="text-green-600 mt-2">{passwordChangeStatus}</div>}
        {passwordChangeError && <div className="text-red-600 mt-2">{passwordChangeError}</div>}
      </div>
    </div>
  );

  const renderIntegrationsSettings = () => (
    <div className="space-y-6">
      <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
        <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">Third-Party Integrations</h4>
        <p className="text-sm text-green-700 dark:text-green-300">
          Configure API keys and settings for external services.
        </p>
      </div>

      {/* Firebase Google Login */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
            <Key className="h-4 w-4 mr-2" />
            Firebase Google Login
          </h4>
          <ToggleField
            label=""
            value={settings.integrations.firebaseEnabled}
            onChange={handleSettingChange('integrations', 'firebaseEnabled')}
          />
        </div>
        {settings.integrations.firebaseEnabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Firebase API Key"
              type="password"
              value={settings.integrations.firebaseApiKey}
              onChange={handleSettingChange('integrations', 'firebaseApiKey')}
              placeholder="AIzaSyC..."
            />
            <InputField
              label="Firebase Auth Domain"
              value={settings.integrations.firebaseAuthDomain}
              onChange={handleSettingChange('integrations', 'firebaseAuthDomain')}
              placeholder="your-project.firebaseapp.com"
            />
            <InputField
              label="Firebase Project ID"
              value={settings.integrations.firebaseProjectId}
              onChange={handleSettingChange('integrations', 'firebaseProjectId')}
              placeholder="your-project-id"
            />
            <InputField
              label="Firebase Storage Bucket"
              value={settings.integrations.firebaseStorageBucket}
              onChange={handleSettingChange('integrations', 'firebaseStorageBucket')}
              placeholder="your-project.appspot.com"
            />
            <InputField
              label="Firebase Messaging Sender ID"
              value={settings.integrations.firebaseMessagingSenderId}
              onChange={handleSettingChange('integrations', 'firebaseMessagingSenderId')}
              placeholder="123456789"
            />
            <InputField
              label="Firebase App ID"
              value={settings.integrations.firebaseAppId}
              onChange={handleSettingChange('integrations', 'firebaseAppId')}
              placeholder="1:123456789:web:abc123"
            />
            <InputField
              label="Firebase Client Email"
              value={settings.integrations.firebaseClientEmail}
              onChange={handleSettingChange('integrations', 'firebaseClientEmail')}
              placeholder="firebase-adminsdk-xxxxx@your-project.iam.gserviceaccount.com"
            />
            <div className="col-span-2">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                💡 <strong>How to get Firebase credentials:</strong> Go to Firebase Console → Project Settings → Service Accounts → Generate new private key
              </p>
            </div>
            <div className="relative">
              <InputField
                label="Firebase Private Key"
                type={showFirebaseKey ? 'text' : 'password'}
                value={settings.integrations.firebasePrivateKey}
                onChange={handleSettingChange('integrations', 'firebasePrivateKey')}
                placeholder="-----BEGIN PRIVATE KEY-----..."
              />
              <button
                type="button"
                onClick={() => setShowFirebaseKey(!showFirebaseKey)}
                className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
              >
                {showFirebaseKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* OpenAI AI Integration */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
            <Brain className="h-4 w-4 mr-2" />
            OpenAI AI Integration
          </h4>
          <ToggleField
            label=""
            value={settings.integrations.aiEnabled}
            onChange={handleSettingChange('integrations', 'aiEnabled')}
          />
        </div>
        {settings.integrations.aiEnabled && (
          <div className="space-y-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                OpenAI API Key <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={settings.integrations.openaiApiKey || ''}
                  onChange={handleSettingChange('integrations', 'openaiApiKey')}
                  placeholder="sk-..."
                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-2 top-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField
                label="Chat Model"
                value={settings.integrations.aiChatModel}
                onChange={handleSettingChange('integrations', 'aiChatModel')}
                options={[
                  { value: 'auto', label: '🔄 Auto (Smart Model Selection)' },
                  { value: 'gpt-5', label: 'GPT-5 (Latest & Most Advanced - 2024)' },
                  { value: 'gpt-5o', label: 'GPT-5o (Latest Omni Model - 2024)' },
                  { value: 'gpt-4o', label: 'GPT-4o (Latest & Most Capable - 2024)' },
                  { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Fast & Cost-effective - 2024)' },
                  { value: 'gpt-4-turbo', label: 'GPT-4 Turbo (High Performance)' },
                  { value: 'gpt-4', label: 'GPT-4 (Standard)' },
                  { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (Fast & Reliable)' }
                ]}
              />
              <SelectField
                label="Content Model"
                value={settings.integrations.aiContentModel}
                onChange={handleSettingChange('integrations', 'aiContentModel')}
                options={[
                  { value: 'auto', label: '🔄 Auto (Smart Model Selection)' },
                  { value: 'gpt-5', label: 'GPT-5 (Latest & Most Advanced - 2024)' },
                  { value: 'gpt-5o', label: 'GPT-5o (Latest Omni Model - 2024)' },
                  { value: 'gpt-4o', label: 'GPT-4o (Latest & Most Capable - 2024)' },
                  { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Fast & Cost-effective - 2024)' },
                  { value: 'gpt-4-turbo', label: 'GPT-4 Turbo (High Performance)' },
                  { value: 'gpt-4', label: 'GPT-4 (Standard)' },
                  { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (Fast & Reliable)' }
                ]}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                label="Max Tokens"
                type="number"
                value={settings.integrations.aiMaxTokens}
                onChange={handleSettingChange('integrations', 'aiMaxTokens')}
                placeholder="1000"
              />
              <InputField
                label="Temperature"
                type="number"
                value={settings.integrations.aiTemperature}
                onChange={handleSettingChange('integrations', 'aiTemperature')}
                placeholder="0.7"
                step="0.1"
                min="0"
                max="2"
              />
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                💡 <strong>AI Features Available:</strong> Chat, Content Generation, Product Descriptions, Blog Writing, SEO Optimization, Translation, Sentiment Analysis, Personalization, Recommendations, Analytics, and Automation.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Google reCAPTCHA moved to Security Management */}

      {/* Google Analytics */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
            <Globe className="h-4 w-4 mr-2" />
            Google Analytics
          </h4>
          <ToggleField
            label=""
            value={settings.integrations.googleAnalyticsEnabled}
            onChange={handleSettingChange('integrations', 'googleAnalyticsEnabled')}
          />
        </div>
        {settings.integrations.googleAnalyticsEnabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
              label="GA4 Tracking ID"
              value={settings.integrations.googleAnalyticsTrackingId}
              onChange={handleSettingChange('integrations', 'googleAnalyticsTrackingId')}
              placeholder="G-XXXXXXXXXX"
            />
            <InputField
              label="GA4 Measurement ID"
              value={settings.integrations.googleAnalyticsMeasurementId}
              onChange={handleSettingChange('integrations', 'googleAnalyticsMeasurementId')}
              placeholder="G-XXXXXXXXXX"
            />
          </div>
        )}
      </div>

      {/* Google Maps */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
            <MapPin className="h-4 w-4 mr-2" />
            Google Maps
          </h4>
          <ToggleField
            label=""
            value={settings.integrations.googleMapsEnabled}
            onChange={handleSettingChange('integrations', 'googleMapsEnabled')}
          />
        </div>
        {settings.integrations.googleMapsEnabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Google Maps API Key"
              type="password"
              value={settings.integrations.googleMapsApiKey}
              onChange={handleSettingChange('integrations', 'googleMapsApiKey')}
              placeholder="AIzaSy..."
            />
            <div className="col-span-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                💡 <strong>How to get Google Maps API key:</strong> Go to Google Cloud Console → APIs & Services → Credentials → Create API Key
              </p>
            </div>
          </div>
        )}
      </div>

      {/* SMTP Email Configuration moved to Email section */}

      {/* OneSignal Push Notifications */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
            <Bell className="h-4 w-4 mr-2" />
            OneSignal Push Notifications
          </h4>
          <ToggleField
            label=""
            value={settings.integrations.onesignalEnabled}
            onChange={handleSettingChange('integrations', 'onesignalEnabled')}
          />
        </div>
        {settings.integrations.onesignalEnabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="OneSignal App ID"
              value={settings.integrations.onesignalAppId}
              onChange={handleSettingChange('integrations', 'onesignalAppId')}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            />
            <div className="relative">
              <InputField
                label="REST API Key"
                type={showApiKey ? 'text' : 'password'}
                value={settings.integrations.onesignalRestApiKey}
                onChange={handleSettingChange('integrations', 'onesignalRestApiKey')}
                placeholder="Your REST API key"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
              >
                {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <InputField
              label="User Auth Key"
              type="password"
              value={settings.integrations.onesignalUserAuthKey}
              onChange={handleSettingChange('integrations', 'onesignalUserAuthKey')}
              placeholder="Your user auth key"
            />
            <InputField
              label="Safari Web ID"
              value={settings.integrations.onesignalSafariWebId}
              onChange={handleSettingChange('integrations', 'onesignalSafariWebId')}
              placeholder="web.onesignal.auto.xxxxxxxx"
            />
          </div>
        )}
      </div>

      {/* Legacy Integrations (keeping for backward compatibility) */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">Legacy Integrations</h4>
        
        {/* Razorpay */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Razorpay Payment Gateway</h5>
          <ToggleField
            label=""
            value={settings.integrations.enableRazorpay}
            onChange={handleSettingChange('integrations', 'enableRazorpay')}
          />
        </div>
          {settings.integrations.enableRazorpay && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField
            label="Razorpay Key ID"
            value={settings.integrations.razorpayKeyId}
            onChange={handleSettingChange('integrations', 'razorpayKeyId')}
            placeholder="rzp_test_XXXXXXXXX"
          />
          <div className="relative">
            <InputField
              label="Razorpay Key Secret"
              type={showApiKey ? 'text' : 'password'}
              value={settings.integrations.razorpayKeySecret}
              onChange={handleSettingChange('integrations', 'razorpayKeySecret')}
              placeholder="Your secret key"
            />
            <button
              type="button"
              onClick={() => setShowApiKey(!showApiKey)}
              className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
            >
              {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
          )}
      </div>

      {/* OpenAI */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">OpenAI Integration</h5>
          <ToggleField
            label=""
            value={settings.integrations.enableOpenAI}
            onChange={handleSettingChange('integrations', 'enableOpenAI')}
          />
        </div>
          {settings.integrations.enableOpenAI && (
        <InputField
          label="OpenAI API Key"
          type="password"
          value={settings.integrations.openaiApiKey}
          onChange={handleSettingChange('integrations', 'openaiApiKey')}
          placeholder="sk-XXXXXXXXX"
        />
          )}
        </div>
      </div>
    </div>
  );

  const renderNotificationsSettings = () => (
    <div className="space-y-6">
      <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
        <h4 className="font-medium text-purple-900 dark:text-purple-100 mb-2">Notification Preferences</h4>
        <p className="text-sm text-purple-700 dark:text-purple-300">
          Configure how and when you receive notifications.
        </p>
      </div>

      <div className="space-y-4">
        <ToggleField
          label="Email Notifications"
          value={settings.notifications.emailNotifications}
          onChange={handleSettingChange('notifications', 'emailNotifications')}
          description="Receive notifications via email"
        />
        <ToggleField
          label="Push Notifications"
          value={settings.notifications.pushNotifications}
          onChange={handleSettingChange('notifications', 'pushNotifications')}
          description="Receive browser push notifications"
        />
        <ToggleField
          label="SMS Notifications"
          value={settings.notifications.smsNotifications}
          onChange={handleSettingChange('notifications', 'smsNotifications')}
          description="Receive notifications via SMS"
        />
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="font-medium text-gray-900 dark:text-white mb-4">Event Notifications</h4>
        <div className="space-y-4">
          <ToggleField
            label="New User Registration"
            value={settings.notifications.newUserRegistration}
            onChange={handleSettingChange('notifications', 'newUserRegistration')}
            description="Notify when new users register"
          />
          <ToggleField
            label="New Contact Inquiry"
            value={settings.notifications.newContactInquiry}
            onChange={handleSettingChange('notifications', 'newContactInquiry')}
            description="Notify when new contact forms are submitted"
          />
          <ToggleField
            label="New Project Submission"
            value={settings.notifications.newProjectSubmission}
            onChange={handleSettingChange('notifications', 'newProjectSubmission')}
            description="Notify when new projects are submitted"
          />
          <ToggleField
            label="Payment Received"
            value={settings.notifications.paymentReceived}
            onChange={handleSettingChange('notifications', 'paymentReceived')}
            description="Notify when payments are received"
          />
          <ToggleField
            label="Project Status Update"
            value={settings.notifications.projectStatusUpdate}
            onChange={handleSettingChange('notifications', 'projectStatusUpdate')}
            description="Notify when project status changes"
          />
          <ToggleField
            label="System Alerts"
            value={settings.notifications.systemAlerts}
            onChange={handleSettingChange('notifications', 'systemAlerts')}
            description="Receive system-level alerts and warnings"
          />
        </div>
      </div>
    </div>
  );

  const renderSystemSettings = () => {
    // Define all website pages
    const websitePages = [
      { key: 'home', label: 'Home Page', path: '/' },
      { key: 'about', label: 'About Us', path: '/about' },
      { key: 'services', label: 'Services', path: '/services' },
      { key: 'products', label: 'Products', path: '/products' },
      { key: 'portfolio', label: 'Portfolio', path: '/portfolio' },
      { key: 'blog', label: 'Blog', path: '/blog' },
      { key: 'contact', label: 'Contact', path: '/contact' },
      { key: 'quote', label: 'Quote', path: '/quote' },
      { key: 'appointment', label: 'Appointment', path: '/appointment' },
      { key: 'cart', label: 'Cart', path: '/cart' },
      { key: 'checkout', label: 'Checkout', path: '/checkout' },
      { key: 'payment', label: 'Payment', path: '/payment' },
      { key: 'thank-you', label: 'Thank You', path: '/thank-you' },
      { key: 'wishlist', label: 'Wishlist', path: '/wishlist' },
      { key: 'privacy-policy', label: 'Privacy Policy', path: '/privacy-policy' },
      { key: 'terms-conditions', label: 'Terms & Conditions', path: '/terms-conditions' },
      { key: 'refund-policy', label: 'Refund Policy', path: '/refund-policy' },
      { key: 'login', label: 'Login', path: '/login' },
      { key: 'register', label: 'Register', path: '/register' },
      { key: 'forgot-password', label: 'Forgot Password', path: '/forgot-password' },
      { key: 'reset-password', label: 'Reset Password', path: '/reset-password' }
    ];

    return (
      <div className="space-y-6">
        <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
          <h4 className="font-medium text-orange-900 dark:text-orange-100 mb-2">System Settings</h4>
          <p className="text-sm text-orange-700 dark:text-orange-300">
            Control website availability and access modes.
          </p>
        </div>

        <div className="space-y-6">
          {/* Global System Mode Settings */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <Settings className="w-5 h-5 mr-2" />
              Global System Mode
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              These settings apply to the entire website. When enabled, all visitors will be redirected to the corresponding page.
            </p>

            {/* Maintenance Mode */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-md font-medium text-gray-900 dark:text-white flex items-center">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Global Maintenance Mode
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Enable maintenance mode for the entire website.
                </p>
              </div>
              <ToggleField
                label=""
                value={settings.system.maintenanceMode}
                onChange={handleSystemModeChange('maintenance')}
              />
            </div>
            
            {settings.system.maintenanceMode && (
              <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Global Maintenance Mode Active
                    </p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                      The entire website is currently in maintenance mode. Only administrators can access the site normally.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Coming Soon Mode */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-md font-medium text-gray-900 dark:text-white flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  Global Coming Soon Mode
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Enable coming soon mode for the entire website.
                </p>
              </div>
              <ToggleField
                label=""
                value={settings.system.comingSoonMode}
                onChange={handleSystemModeChange('comingSoon')}
              />
            </div>
            
            {settings.system.comingSoonMode && (
              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-start">
                  <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      Global Coming Soon Mode Active
                    </p>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                      The entire website is currently in coming soon mode. Only administrators can access the site normally.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Individual Page Settings */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Individual Page Settings
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Control maintenance and coming soon modes for specific pages. These settings override global settings for individual pages.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:p-6">
              {websitePages.map((page) => (
                <div key={page.key} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      {page.label}
                    </h4>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {page.path}
                    </span>
                  </div>
                  
                  <div className="space-y-3">
                    {/* Page Maintenance Mode */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <RefreshCw className="w-4 h-4 text-orange-500 mr-2" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">Maintenance</span>
                      </div>
                      <ToggleField
                        label=""
                        value={settings.system[`${page.key}MaintenanceMode`] || false}
                        onChange={handlePageModeChange(page.key, 'maintenance')}
                      />
                    </div>

                    {/* Page Coming Soon Mode */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 text-blue-500 mr-2" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">Coming Soon</span>
                      </div>
                      <ToggleField
                        label=""
                        value={settings.system[`${page.key}ComingSoonMode`] || false}
                        onChange={handlePageModeChange(page.key, 'comingSoon')}
                      />
                    </div>
                  </div>

                  {/* Status indicators */}
                  {(settings.system[`${page.key}MaintenanceMode`] || settings.system[`${page.key}ComingSoonMode`]) && (
                    <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-700 rounded text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Status:</span>
                        <div className="flex space-x-2">
                          {settings.system[`${page.key}MaintenanceMode`] && (
                            <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 rounded">
                              Maintenance
                            </span>
                          )}
                          {settings.system[`${page.key}ComingSoonMode`] && (
                            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded">
                              Coming Soon
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Debug info */}
                  {process.env.NODE_ENV === 'development' && (
                    <div className="mt-2 p-1 bg-gray-100 dark:bg-gray-800 rounded text-xs text-gray-500">
                      <div>Debug: {page.key}MaintenanceMode = {String(settings.system[`${page.key}MaintenanceMode`])}</div>
                      <div>Debug: {page.key}ComingSoonMode = {String(settings.system[`${page.key}ComingSoonMode`])}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Important Notice */}
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-3" />
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Important Notice
                </p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  <strong>Global modes</strong> override individual page settings. When a global mode is enabled, 
                  all pages will redirect regardless of individual page settings. Individual page settings only work 
                  when global modes are disabled. Make sure to disable global modes when you want to use individual 
                  page settings.
                </p>
              </div>
            </div>
          </div>

          {/* Debug Test Section */}
          {process.env.NODE_ENV === 'development' && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    Debug Test Section
                  </p>
                  <div className="mt-2 space-y-2">
                    <button
                      onClick={() => {
                        console.log('🔍 Current system settings:', settings.system);
                        console.log('🔍 Page-level settings:', Object.keys(settings.system).filter(key => 
                          key.includes('MaintenanceMode') || key.includes('ComingSoonMode')
                        ));
                      }}
                      className="px-3 py-1 bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 rounded text-xs"
                    >
                      Log Current Settings
                    </button>
                    <button
                      onClick={() => {
                        setSettings(prev => ({
                          ...prev,
                          system: {
                            ...prev.system,
                            homeMaintenanceMode: !prev.system.homeMaintenanceMode
                          }
                        }));
                      }}
                      className="px-3 py-1 bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded text-xs ml-2"
                    >
                      Toggle Home Maintenance
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAppearanceSettings = () => (
    <div className="space-y-6">
      <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
        <h4 className="font-medium text-orange-900 dark:text-orange-100 mb-2">Appearance Settings</h4>
        <p className="text-sm text-orange-700 dark:text-orange-300">
          Customize the look and feel of your application.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SelectField
          label="Theme"
          value={settings.appearance.theme}
          onChange={handleSettingChange('appearance', 'theme')}
          options={[
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
            { value: 'auto', label: 'Auto (System)' }
          ]}
        />
        <ToggleField
          label="Enable Dark Mode"
          value={settings.appearance.enableDarkMode}
          onChange={handleSettingChange('appearance', 'enableDarkMode')}
          description="Allow users to switch to dark mode"
        />
      </div>

      {/* Color options hidden - removed as requested */}
      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <InputField
          label="Primary Color"
          type="color"
          value={settings.appearance.primaryColor}
          onChange={handleSettingChange('appearance', 'primaryColor')}
        />
        <InputField
          label="Secondary Color"
          type="color"
          value={settings.appearance.secondaryColor}
          onChange={handleSettingChange('appearance', 'secondaryColor')}
        />
        <InputField
          label="Accent Color"
          type="color"
          value={settings.appearance.accentColor}
          onChange={handleSettingChange('appearance', 'accentColor')}
        />
      </div> */}

      {/* Logo Management */}
      <div className="space-y-6">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white">Logo Management</h4>
        
        {/* Light Mode Logo */}
        <div className="space-y-4">
          <h5 className="text-md font-medium text-gray-700 dark:text-gray-300">Light Mode Logo</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Logo URL"
              value={settings.appearance.logoLight}
              onChange={handleSettingChange('appearance', 'logoLight')}
              placeholder="https://example.com/logo-light.png"
            />
            <InputField
              label="Logo File Path"
              value={settings.appearance.logoLightFile}
              onChange={handleSettingChange('appearance', 'logoLightFile')}
              placeholder="/uploads/logos/logo-light.png"
            />
          </div>
        </div>
        
        {/* Dark Mode Logo */}
        <div className="space-y-4">
          <h5 className="text-md font-medium text-gray-700 dark:text-gray-300">Dark Mode Logo</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Logo URL"
              value={settings.appearance.logoDark}
              onChange={handleSettingChange('appearance', 'logoDark')}
              placeholder="https://example.com/logo-dark.png"
            />
            <InputField
              label="Logo File Path"
              value={settings.appearance.logoDarkFile}
              onChange={handleSettingChange('appearance', 'logoDarkFile')}
              placeholder="/uploads/logos/logo-dark.png"
            />
          </div>
        </div>
        
        {/* Favicon */}
        <div className="space-y-4">
          <h5 className="text-md font-medium text-gray-700 dark:text-gray-300">Favicon</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Favicon URL"
              value={settings.appearance.favicon}
              onChange={handleSettingChange('appearance', 'favicon')}
              placeholder="https://example.com/favicon.ico"
            />
            <InputField
              label="Favicon File Path"
              value={settings.appearance.faviconFile}
              onChange={handleSettingChange('appearance', 'faviconFile')}
              placeholder="/uploads/favicon.ico"
            />
          </div>
        </div>
      </div>

      {/* PWA Theme Settings */}
      <div className="space-y-6">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white">PWA Theme Settings</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Configure colors for Progressive Web App installation and browser UI.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField
            label="Theme Color"
            type="color"
            value={settings.appearance.themeColor || '#3B82F6'}
            onChange={handleSettingChange('appearance', 'themeColor')}
            description="Color for browser UI elements (address bar, etc.)"
          />
          <InputField
            label="Background Color"
            type="color"
            value={settings.appearance.backgroundColor || '#ffffff'}
            onChange={handleSettingChange('appearance', 'backgroundColor')}
            description="Background color for PWA splash screen"
          />
        </div>
      </div>

      <TextareaField
        label="Custom CSS"
        value={settings.appearance.customCSS}
        onChange={handleSettingChange('appearance', 'customCSS')}
        placeholder="Add custom CSS styles here..."
        rows={6}
      />
    </div>
  );

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Settings className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
          Settings Management
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
          Configure system settings and preferences
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 sm:p-6">
        {/* Sidebar */}
        <div className="lg:w-64 flex-shrink-0">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-3" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
            {activeTab === 'general' && renderGeneralSettings()}
            {activeTab === 'email' && renderEmailSettings()}
            {activeTab === 'notifications' && renderNotificationsSettings()}
            {activeTab === 'system' && renderSystemSettings()}
            {activeTab === 'integrations' && renderIntegrationsSettings()}
            {activeTab === 'appearance' && renderAppearanceSettings()}
            
            {/* Save Message */}
            {saveMessage && (
              <div className={`mt-4 p-3 rounded-lg flex items-center ${
                saveStatus === 'success' 
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
                  : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
              }`}>
                {saveStatus === 'success' ? (
                  <CheckCircle className="h-4 w-4 mr-2" />
                ) : (
                  <AlertTriangle className="h-4 w-4 mr-2" />
                )}
                {saveMessage}
              </div>
            )}

            {/* Save Button */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                  <AlertTriangle className="h-4 w-4 mr-2 text-yellow-500" />
                  Click "Save Changes" to apply your settings
                </div>
                <button
                  onClick={() => handleSave(activeTab)}
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsManagement;
